import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Chakra imports
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Badge,
  Divider,
  Icon,
  SimpleGrid,
  Card,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';

// Services
import tuitionService from 'services/tuitionService';
import { formatCurrency, formatDate, formatStatus, formatPaymentMethod } from 'utils/format';

// Icons
import { MdArrowBack, MdPayment, MdCalendarToday, MdAttachMoney, MdCheckCircle, MdSchool, MdEdit, MdDelete, MdVerified } from 'react-icons/md';

export default function ViewPayment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  // Chakra Color Mode
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const bgStatus = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
  const warningBg = useColorModeValue('red.50', 'red.900');
  const warningBorder = useColorModeValue('red.200', 'red.700');
  const warningText = useColorModeValue('red.600', 'red.300');

  // State
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: '',
    payment_method: '',
    reference_number: '',
    notes: '',
    status: '',
  });
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [validating, setValidating] = useState(false);
  
  // Modals
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef();

  // Fetch payment data
  useEffect(() => {
    const fetchPayment = async () => {
      if (!id) {
        setError('Payment ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await tuitionService.getPayment(id);

        if (result.success && result.data) {
          setPayment(result.data);
        } else {
          setError(result.error || 'Failed to load payment');
          toast({
            title: 'Error',
            description: result.error || 'Failed to load payment',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (err) {
        console.error('Error fetching payment:', err);
        setError('Failed to load payment. Please try again.');
        toast({
          title: 'Error',
          description: 'Failed to load payment. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPayment();
  }, [id, toast]);

  // Initialize form data when payment is loaded
  useEffect(() => {
    if (payment) {
      setFormData({
        amount: payment.amount || '',
        payment_date: payment.payment_date ? payment.payment_date.split('T')[0] : '',
        payment_method: payment.payment_method || '',
        reference_number: payment.reference_number || '',
        notes: payment.notes || '',
        status: payment.status || '',
      });
    }
  }, [payment]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle update payment
  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const updateData = {
        ...formData,
        payment_date: formData.payment_date ? new Date(formData.payment_date).toISOString() : undefined,
        amount: formData.amount ? Number(formData.amount) : undefined,
      };

      // Remove empty fields
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '' || updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      const result = await tuitionService.updatePaymentPartial(id, updateData);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Payment updated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onEditClose();
        // Refresh payment data
        const fetchResult = await tuitionService.getPayment(id);
        if (fetchResult.success && fetchResult.data) {
          setPayment(fetchResult.data);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update payment',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Update error:', err);
      toast({
        title: 'Error',
        description: 'Failed to update payment. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete payment
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const result = await tuitionService.deletePayment(id);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Payment deleted successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onDeleteClose();
        // Navigate back to tuition page if available, otherwise go back
        if (payment?.tuition_id) {
          navigate(`/admin/tuition/${payment.tuition_id}`);
        } else {
          navigate(-1);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to delete payment',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete payment. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle validate payment
  const handleValidate = async () => {
    setValidating(true);
    try {
      const result = await tuitionService.validatePayment(id);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Payment validated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // Refresh payment data
        const fetchResult = await tuitionService.getPayment(id);
        if (fetchResult.success && fetchResult.data) {
          setPayment(fetchResult.data);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to validate payment',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Validate error:', err);
      toast({
        title: 'Error',
        description: 'Failed to validate payment. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Center minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Center>
      </Box>
    );
  }

  if (error || !payment) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Card p='40px' bg={cardBg}>
          <Flex direction='column' align='center' gap='20px'>
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              {error || 'Payment not found'}
            </Text>
            <Button onClick={() => navigate(-1)} leftIcon={<Icon as={MdArrowBack} />}>
              Go Back
            </Button>
          </Flex>
        </Card>
      </Box>
    );
  }

  const statusColorScheme = 
    payment.status === 'validated' ? 'green' : 
    payment.status === 'pending' ? 'orange' : 
    payment.status === 'rejected' ? 'red' : 'brand';

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        {/* Back, Edit, Delete, and Validate Buttons */}
        <Flex justify='space-between' align='center' mb='10px' flexWrap='wrap' gap='10px'>
          <Button
            onClick={() => navigate(-1)}
            leftIcon={<Icon as={MdArrowBack} />}
            variant='ghost'>
            Go Back
          </Button>
          <Flex gap='10px' flexWrap='wrap'>
            {payment?.status !== 'validated' && (
              <Button
                onClick={handleValidate}
                leftIcon={<Icon as={MdVerified} />}
                colorScheme='green'
                variant='solid'
                isLoading={validating}
                loadingText="Validating">
                Validate Payment
              </Button>
            )}
            <Button
              onClick={onEditOpen}
              leftIcon={<Icon as={MdEdit} />}
              colorScheme='blue'
              variant='solid'>
              Edit Payment
            </Button>
            <Button
              onClick={onDeleteOpen}
              leftIcon={<Icon as={MdDelete} />}
              colorScheme='red'
              variant='solid'>
              Delete Payment
            </Button>
          </Flex>
        </Flex>

        {/* Payment Header */}
        <Card p='30px' bg={cardBg}>
          <Flex direction='column' gap='20px'>
            <Flex justify='space-between' align='center' flexWrap='wrap' gap='15px'>
              <Flex direction='column'>
                <Text color={textColor} fontSize='2xl' fontWeight='700' mb='5px'>
                  Payment Details
                </Text>
                <Text color={textColorSecondary} fontSize='sm'>
                  ID: {payment.id}
                </Text>
              </Flex>
              <Badge 
                colorScheme={statusColorScheme} 
                bg={bgStatus} 
                fontSize="md" 
                fontWeight="700" 
                px="15px" 
                py="5px"
              >
                {formatStatus(payment.status)}
              </Badge>
            </Flex>
            <Divider borderColor={borderColor} />
          </Flex>
        </Card>

        {/* Payment Information */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdPayment} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Payment Information
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdAttachMoney} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Amount
                </Text>
              </Flex>
              <Text color={textColor} fontSize='lg' fontWeight='700'>
                {formatCurrency(payment.amount)}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdCalendarToday} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Payment Date
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {formatDate(payment.payment_date)}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Payment Method
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {formatPaymentMethod(payment.payment_method)}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Reference Number
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600' fontFamily="mono">
                {payment.reference_number || 'N/A'}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Status
              </Text>
              <Badge 
                colorScheme={statusColorScheme} 
                bg={bgStatus} 
                fontSize="sm" 
                fontWeight="700" 
                px="10px" 
                py="3px"
              >
                {formatStatus(payment.status)}
              </Badge>
            </Box>
            {payment.notes && (
              <Box gridColumn={{ base: '1', md: '1 / 3' }}>
                <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                  Notes
                </Text>
                <Text color={textColor} fontSize='md' fontWeight='500' whiteSpace="pre-wrap">
                  {payment.notes}
                </Text>
              </Box>
            )}
          </SimpleGrid>
        </Card>

        {/* Validation Information */}
        {payment.status === 'validated' && (
          <Card p='30px' bg={cardBg}>
            <Flex align='center' gap='10px' mb='20px'>
              <Icon as={MdCheckCircle} color='green.500' fontSize='xl' />
              <Text color={textColor} fontSize='xl' fontWeight='600'>
                Validation Information
              </Text>
            </Flex>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
              <Box>
                <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                  Validated By
                </Text>
                <Text color={textColor} fontSize='md' fontWeight='600'>
                  {payment.validated_by_name || payment.validated_by || 'N/A'}
                </Text>
              </Box>
              <Box>
                <Flex align='center' gap='5px' mb='5px'>
                  <Icon as={MdCalendarToday} color={textColorSecondary} fontSize='sm' />
                  <Text color={textColorSecondary} fontSize='sm'>
                    Validated At
                  </Text>
                </Flex>
                <Text color={textColor} fontSize='md' fontWeight='500'>
                  {formatDate(payment.validated_at)}
                </Text>
              </Box>
            </SimpleGrid>
          </Card>
        )}

        {/* Tuition Information */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdSchool} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Related Tuition
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Tuition ID
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600' fontFamily="mono">
                {payment.tuition_id}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Tuition Amount
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {formatCurrency(payment.tuition_amount)}
              </Text>
            </Box>
            <Box gridColumn={{ base: '1', md: '1 / 3' }}>
              <Button
                onClick={() => navigate(`/admin/tuition/${payment.tuition_id}`)}
                leftIcon={<Icon as={MdSchool} />}
                colorScheme='blue'
                variant='outline'
                size='sm'>
                View Tuition Details
              </Button>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Timestamps */}
        <Card p='30px' bg={cardBg}>
          <Text color={textColor} fontSize='xl' fontWeight='600' mb='20px'>
            Timestamps
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Created At
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='500'>
                {formatDate(payment.created_at)}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Last Updated
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='500'>
                {formatDate(payment.updated_at)}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>
      </Flex>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor} fontSize="2xl" fontWeight="700">
            Edit Payment
          </ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody pb="24px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Amount
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="Enter payment amount"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Payment Date
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="date"
                  name="payment_date"
                  value={formData.payment_date}
                  onChange={handleInputChange}
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Payment Method
                </FormLabel>
                <Select
                  variant='main'
                  fontSize='sm'
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleInputChange}
                  placeholder="Select payment method"
                  mb='8px'
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="check">Check</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Status
                </FormLabel>
                <Select
                  variant='main'
                  fontSize='sm'
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  placeholder="Select status"
                  mb='8px'
                >
                  <option value="pending">Pending</option>
                  <option value="validated">Validated</option>
                  <option value="rejected">Rejected</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Reference Number
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="reference_number"
                  value={formData.reference_number}
                  onChange={handleInputChange}
                  placeholder="Transaction reference or receipt number"
                  mb='8px'
                />
              </FormControl>

              <FormControl gridColumn={{ base: '1', md: '1 / 3' }}>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Notes
                </FormLabel>
                <Textarea
                  variant='main'
                  fontSize='sm'
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional notes (optional)"
                  rows={3}
                  mb='8px'
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>

          <ModalFooter>
            <Button
              variant='light'
              fontWeight='500'
              mr={3}
              onClick={onEditClose}
              isDisabled={updating}>
              Cancel
            </Button>
            <Button
              variant='brand'
              fontWeight='500'
              onClick={handleUpdate}
              isLoading={updating}
              loadingText="Updating">
              Update Payment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader fontSize="xl" fontWeight="700" color={textColor}>
              Delete Payment
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text color={textColor} fontSize="md" mb="10px">
                Are you sure you want to delete this payment? This action cannot be undone.
              </Text>
              {payment && (
                <Box
                  mt="15px"
                  p="12px"
                  borderRadius="8px"
                  bg={warningBg}
                  border="1px solid"
                  borderColor={warningBorder}>
                  <Text color={warningText} fontSize="sm" fontWeight="500">
                    ⚠️ This will permanently delete the payment record.
                  </Text>
                </Box>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant='light'
                fontWeight='500'
                onClick={onDeleteClose}
                isDisabled={deleting}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                variant='solid'
                fontWeight='500'
                onClick={handleDelete}
                ml={3}
                isLoading={deleting}
                loadingText="Deleting">
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

